import { useEffect, useRef } from 'react';

export interface PresenceEvent {
  eveCharacterId: number;
  characterName: string;
  accountId: number;
  solarSystemId: number;
  systemName: string;
  systemClass: string | null;
  enteredAt: string;
}

interface Subscriber {
  onEvent: (event: PresenceEvent) => void;
  onReconnect?: () => void;
}

/** One EventSource for the whole app. Every mounted subscriber shares it, so the
 * stream is open whenever anyone needs it, not only while the dashboard is on
 * screen — before this, a move while on a system page reached nothing at all.
 * The browser reconnects on its own; every (re)open tells every subscriber to
 * re-read, because EventSource has no replay. */
const subscribers = new Set<Subscriber>();
let source: EventSource | null = null;
let closeTimer: ReturnType<typeof setTimeout> | null = null;

function openSource() {
  if (source) return;
  const next = new EventSource('/api/characters/presence/stream');
  next.onopen = () => subscribers.forEach((s) => s.onReconnect?.());
  next.onmessage = (message) => {
    let event: PresenceEvent;
    try {
      event = JSON.parse(message.data) as PresenceEvent;
    } catch {
      // A malformed frame is not worth breaking the stream over.
      return;
    }
    subscribers.forEach((s) => s.onEvent(event));
  };
  source = next;
}

function scheduleClose() {
  // Leaving one page for another unmounts a subscriber and mounts the next in
  // the same commit. Waiting a tick lets the new one take over the open stream
  // instead of tearing it down and reconnecting.
  closeTimer = setTimeout(() => {
    closeTimer = null;
    if (subscribers.size === 0) {
      source?.close();
      source = null;
    }
  }, 0);
}

/** Live "one of your characters changed systems" push, from the backend's
 * presence poller (services/presencePoller.ts) over SSE. Native EventSource, so
 * no dependency, and the session cookie rides along on the same origin.
 *
 * The handlers are held in refs so a caller passing an inline arrow function
 * doesn't re-subscribe on every render. */
export function usePresenceStream(
  onEvent: (event: PresenceEvent) => void,
  enabled = true,
  /** Called on every (re)connection. EventSource has no replay, so the caller
   * re-reads anything that moved while the stream was not open. */
  onReconnect?: () => void,
) {
  const handler = useRef(onEvent);
  handler.current = onEvent;
  const reconnected = useRef(onReconnect);
  reconnected.current = onReconnect;

  useEffect(() => {
    if (!enabled) return;
    const subscriber: Subscriber = {
      onEvent: (event) => handler.current(event),
      onReconnect: () => reconnected.current?.(),
    };
    subscribers.add(subscriber);
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    openSource();
    return () => {
      subscribers.delete(subscriber);
      if (subscribers.size === 0) scheduleClose();
    };
  }, [enabled]);
}
